/**
 * System Configuration - RTK Query API
 * API endpoints for system configuration management using Django REST Framework
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

// SHR-15: getApiVersion pointed at '/api/v1/health/version/', a route
// that does not exist (apps/shared/urls.py only registers
// '/api/v1/health/' -- see apps/shared/views.py::health_check), which
// already returns a 'version' field in its response body alongside
// status/database/cache/etc. Exported via systemConfigApiTestUtils
// because RTK Query does not expose a query's raw transformResponse
// config on the built api.endpoints[name] object for direct inspection.
const apiVersionTransformResponse = (response) => ({
  version: response?.version,
});

export const systemConfigApi = createApi({
  reducerPath: 'systemConfigApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['SystemConfig'],
  endpoints: (builder) => ({
    // =========================================================================
    // System Configuration
    // =========================================================================

    /**
     * Get current system configuration
     */
    getSystemConfig: builder.query({
      query: () => ({
        url: '/api/v1/auth/config/',
      }),
      providesTags: ['SystemConfig'],
    }),

    /**
     * Update system configuration (admin only)
     */
    updateSystemConfig: builder.mutation({
      query: (data) => ({
        url: '/api/v1/auth/config/',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['SystemConfig'],
    }),

    // =========================================================================
    // Feature Flags
    // =========================================================================

    /**
     * Get feature flags for current tenant
     */
    getFeatureFlags: builder.query({
      query: () => ({
        url: '/api/v1/auth/features/',
      }),
    }),

    // =========================================================================
    // Performance (staff/superuser only - real host metrics via psutil)
    // =========================================================================

    getSystemPerformance: builder.query({
      query: () => ({
        url: '/api/v1/auth/system/performance/',
      }),
    }),

    // =========================================================================
    // Health & Status
    // =========================================================================

    /**
     * Get system health status
     */
    getHealthStatus: builder.query({
      query: () => ({
        url: '/api/v1/health/',
      }),
    }),

    /**
     * Get API version info
     */
    getApiVersion: builder.query({
      query: () => ({
        url: '/api/v1/health/',
      }),
      transformResponse: apiVersionTransformResponse,
    }),
  }),
});

export const {
  // System Config
  useGetSystemConfigQuery,
  useUpdateSystemConfigMutation,
  // Feature Flags
  useGetFeatureFlagsQuery,
  // Performance
  useGetSystemPerformanceQuery,
  // Health
  useGetHealthStatusQuery,
  useGetApiVersionQuery,
} = systemConfigApi;

export const systemConfigApiTestUtils = { apiVersionTransformResponse };

export default systemConfigApi;
